import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';

export type RunLeader = {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  profile_image: string | null;
  is_admin: boolean;
  created_at?: string | null;
};

export type Profile = {
  id: string;
  display_name: string | null;
  username: string | null;
  bio: string | null;
  location: string | null;
  profile_image: string | null;
  instagram_handle: string | null;
  strava_link: string | null;
  pace_range: string | null;
  weekly_mileage: string | null;
  experience_level: string | null;
  neighborhood: string | null;
  goals: string[] | null;
  created_at?: string | null;
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  leader: RunLeader | null;
  profile: Profile | null;
  loading: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  signInWithPassword: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  setPassword: (password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshLeader: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** club_ids this leader manages (club_leaders membership); admins manage all */
  managedClubIds: string[];
  canManageClub: (clubId?: string | null) => boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function fetchLeader(userId: string): Promise<RunLeader | null> {
  const { data, error } = await supabase
    .from('run_leaders')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch run leader:', error.message);
    return null;
  }

  return data;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch profile:', error.message);
    return null;
  }

  return data;
}

async function ensureProfile(user: User): Promise<Profile | null> {
  const existing = await fetchProfile(user.id);

  const googleName =
    (typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : null) ||
    (typeof user.user_metadata?.name === 'string' ? user.user_metadata.name : null) ||
    null;
  const googleAvatar =
    (typeof user.user_metadata?.avatar_url === 'string' ? user.user_metadata.avatar_url : null) ||
    (typeof user.user_metadata?.picture === 'string' ? user.user_metadata.picture : null) ||
    null;

  // Nothing to fill in from provider metadata
  if (!googleName && !googleAvatar) return existing;

  if (!existing) {
    // New user — create profile seeded with Google data
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        email: user.email ?? null,
        display_name: googleName,
        profile_image: googleAvatar,
      })
      .select('*')
      .single();
    if (error) console.error('Failed to create profile from provider:', error.message);
    return (data as Profile | null) ?? null;
  }

  // Existing user — only fill fields that are still blank
  const patch: Record<string, string> = {};
  if (!existing.display_name && googleName) patch.display_name = googleName;
  if (!existing.profile_image && googleAvatar) patch.profile_image = googleAvatar;

  if (Object.keys(patch).length === 0) return existing;

  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', user.id)
    .select('*')
    .single();
  if (error) console.error('Failed to patch profile from provider:', error.message);
  return (data as Profile | null) ?? existing;
}

async function claimPendingLeaderInvites(user: User): Promise<RunLeader | null> {
  // Handled server-side: the RPC re-checks the invite against the signed-in email,
  // creates (or adopts) the run_leaders row, attaches the club and marks the invite
  // claimed in one transaction. RLS deliberately blocks these writes from the client.
  const { data, error } = await supabase.rpc('claim_pending_leader_invites');

  if (error) {
    console.error('Failed to claim pending leader invites:', error.message);
    return fetchLeader(user.id);
  }

  const claimed = (Array.isArray(data) ? data[0] : data) as RunLeader | null;

  if (claimed?.id) {
    return claimed;
  }

  return fetchLeader(user.id);
}

async function fetchManagedClubIds(leaderId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('club_leaders')
    .select('club_id')
    .eq('leader_id', leaderId);

  if (error) {
    console.error('Failed to load managed clubs:', error.message);
    return [];
  }

  return ((data || []) as { club_id: string }[]).map((row) => row.club_id);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [leader, setLeader] = useState<RunLeader | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [managedClubIds, setManagedClubIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshLeader = useCallback(async () => {
    if (!user) {
      setLeader(null);
      setManagedClubIds([]);
      return;
    }

    const leaderRow = await fetchLeader(user.id);
    setLeader(leaderRow);
    setManagedClubIds(leaderRow ? await fetchManagedClubIds(leaderRow.id) : []);
  }, [user]);

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }

    const profileRow = await fetchProfile(user.id);
    setProfile(profileRow);
  }, [user]);

  const hydrateFromSession = useCallback(async (nextSession: Session | null) => {
    try {
      setLoading(true);
      setSession(nextSession);

      const nextUser = nextSession?.user ?? null;
      setUser(nextUser);

      if (!nextUser) {
        setLeader(null);
        setProfile(null);
        setManagedClubIds([]);
        return;
      }

      const [leaderRow, profileRow] = await Promise.all([
        claimPendingLeaderInvites(nextUser),
        ensureProfile(nextUser),
      ]);

      setLeader(leaderRow);
      setProfile(profileRow);
      setManagedClubIds(leaderRow ? await fetchManagedClubIds(leaderRow.id) : []);
    } catch (error) {
      console.error('Failed to hydrate auth session:', error);
      setLeader(null);
      setProfile(null);
      setManagedClubIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();

        if (!mounted) return;
        await hydrateFromSession(currentSession);
      } catch (error) {
        console.error('Initial auth load failed:', error);
        if (mounted) {
          setLeader(null);
          setProfile(null);
          setUser(null);
          setSession(null);
          setLoading(false);
        }
      }
    }

    load();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      hydrateFromSession(nextSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateFromSession]);

  async function signInWithMagicLink(email: string) {
    const redirectTo = `${window.location.origin}/auth`;

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo,
      },
    });

    return { error: error?.message ?? null };
  }

  async function signInWithPassword(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error?.message ?? null };
  }

  async function setPassword(password: string) {
    const { error } = await supabase.auth.updateUser({
      password,
    });

    return { error: error?.message ?? null };
  }

  async function signOut() {
    try {
      await supabase.auth.signOut();
    } finally {
      setLeader(null);
      setProfile(null);
      setManagedClubIds([]);
      setUser(null);
      setSession(null);
      setLoading(false);
    }
  }

  const canManageClub = useCallback(
    (clubId?: string | null) => {
      if (!leader) return false;
      if (leader.is_admin) return true;
      if (!clubId) return false;
      return managedClubIds.includes(clubId);
    },
    [leader, managedClubIds]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      leader,
      profile,
      loading,
      signInWithMagicLink,
      signInWithPassword,
      setPassword,
      signOut,
      refreshLeader,
      refreshProfile,
      managedClubIds,
      canManageClub,
    }),
    [
      session,
      user,
      leader,
      profile,
      loading,
      refreshLeader,
      refreshProfile,
      managedClubIds,
      canManageClub,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}